import { CONFIGURATION_APIS } from "@/api_framework/api_config";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import axios, { AxiosResponse } from "axios";
import AxiosCurlirize from "axios-curlirize";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useAPIUtils } from "../../useAPIUtils";
import { useCurlLogger } from "../../useCurlLogger";

export type IClinicImageCategory = string[] | { base_64: string[]; image_extension: string } | null;

export interface IConfigurationClinicImagesData {
  clinic_id: string;
  image_url?: string;
  image_version?: { image_base64: string; image_extension: string } | null;
  clinic_listing_page: Record<string, IClinicImageCategory> | null;
  clinic_details_page: Record<string, IClinicImageCategory> | null;
}

interface IConfigurationClinicImagesResponse {
  status: boolean;
  message: string;
  data?: IConfigurationClinicImagesData | null;
}

AxiosCurlirize(axios, () => {});

export const useConfigurationClinicImagesAPI = () => {
  const [clinicImages, setClinicImages] = useState<IConfigurationClinicImagesData | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const configuration = useConfiguration();
  const { sendCurl } = useCurlLogger();
  const { captureError } = useAPIUtils();

  const execute = useCallback(
    async (clinicId: string) => {
      if (!clinicId) {
        return;
      }
      setIsFetching(true);
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const res: AxiosResponse<IConfigurationClinicImagesResponse> = await axios.get(
          `${CONFIGURATION_APIS?.CLINIC_IMAGES?.baseURL}${clinicId}/`,
          {
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (res.data?.status) {
          setClinicImages(res.data?.data ?? null);
        } else {
          toast.error(res.data?.message);
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.config?.["curlCommand"]) {
            sendCurl(error.config["curlCommand"]);
          }
          captureError(error);
          toast.error((error.response?.data as any)?.message || error.message);
        } else {
          toast.error("Server Error: " + (error as Error).message);
        }
      } finally {
        setIsFetching(false);
      }
    },
    [captureError, configuration, sendCurl],
  );

  return { clinicImages, isFetching, execute };
};
